import type { Ssm5Diagram } from '@packages/domain/ssm5';

import { getLogger } from '@packages/core/logging';
import { GetDiagram } from '@packages/domain/ssm5';
import { container } from '@web/services/container';

import type { Ssm5Read, Ssm5ReadState } from './use-ssm5-read';

import { useSsm5Read } from './use-ssm5-read';

const [$getDiagram] = container.resolveOperations(GetDiagram);

const logger = getLogger('ssm5');

/** The diagram read of a session, and the way to read it again. */
export type Ssm5DiagramRead = Ssm5Read<SettledRead>;

/** Where the diagram read of a session stands. */
export type Ssm5DiagramState = Ssm5ReadState<SettledRead>;

/** A diagram read that has settled. */
type SettledRead =
    | {
          /** Marks a read that failed. */
          status: 'failed';
      }
    | {
          /** Marks a completed read. */
          status: 'loaded';
          /** What the read found. */
          diagram: Ssm5Diagram;
      };

const FAILED: SettledRead = { status: 'failed' };

/**
 * Read the CAN Bus diagram of the session behind a Cloud Connector ID: once per ID, again on reload,
 * aborted when the ID changes or the component unmounts. Each read replaces what the previous one
 * found, so nothing stale outlives a failure.
 *
 * @param cloudConnectorId - the Cloud Connector ID of the session, or undefined without one
 * @returns where the read stands, and the reload
 */
export function useSsm5Diagram(cloudConnectorId: string | undefined): Ssm5DiagramRead {
    return useSsm5Read(cloudConnectorId, readDiagram);
}

/**
 * Read the diagram of a session. A read that throws, aborted or not, reads as a failure; a caller that
 * aborted ignores the answer anyway. A throw that no abort caused is logged, as the DTC read does, since
 * nothing else reports it.
 *
 * @param cloudConnectorId - the Cloud Connector ID of the session
 * @param signal - aborts the read
 * @returns how the read settled
 */
async function readDiagram(cloudConnectorId: string, signal: AbortSignal): Promise<SettledRead> {
    try {
        const diagram = await $getDiagram({ cloudConnectorId, signal });

        return { diagram, status: 'loaded' };
    } catch (error: unknown) {
        if (!signal.aborted) {
            logger.error(`The SSM5 diagram read through [${cloudConnectorId}] failed before it settled`, {
                cloudConnectorId,
                error,
            });
        }

        return FAILED;
    }
}
